import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import { useState } from 'react'
import { parseMarkdown } from '../utils/markdownConfig'
import { AnnotationTooltip } from '../components/AnnotationTooltip'
import { useMermaidAsync } from '../hooks/useMermaidAsync'
import { useD2Async } from '../hooks/useD2Async'
import '../styles/global.css'

export function ShareView() {
  const { id } = useParams<{ id: string }>()
  const [annotationText, setAnnotationText] = useState('')
  const [readModeRoot, setReadModeRoot] = useState<HTMLElement | null>(null)
  useMermaidAsync(readModeRoot)
  useD2Async(readModeRoot)
  const [lastSelection, setLastSelection] = useState('')
  const [selectionLine, setSelectionLine] = useState<number | null>(null)
  const [showAnnotationForm, setShowAnnotationForm] = useState(false)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const [authorName, setAuthorName] = useState(() => localStorage.getItem('share_author') || '')
  const [showNamePrompt, setShowNamePrompt] = useState(() => !localStorage.getItem('share_author'))

  const { data: post, isLoading } = useQuery({
    queryKey: ['post', id],
    queryFn: () => apiClient.getPost(Number(id)),
    enabled: !!id,
  })

  const handleSubmitAnnotation = async () => {
    if (!post || !annotationText.trim() || !lastSelection) return
    // Find position in source
    const src = post.content || ''
    const startPos = src.toLowerCase().indexOf(lastSelection.slice(0, 30).toLowerCase())
    await apiClient.createAnnotation(post.id, {
      start_pos: startPos >= 0 ? startPos : 0,
      end_pos: startPos >= 0 ? startPos + lastSelection.length : 0,
      note_text: annotationText.trim(),
      author_id: authorName.toLowerCase().replace(/\s+/g, '-') || 'guest',
      author_name: authorName || 'Guest',
    })
    setAnnotationText('')
    setShowAnnotationForm(false)
    setLastSelection('')
    // Reload annotations
    window.location.reload()
  }

  if (showNamePrompt) {
    return (
      <div className="share-name-prompt">
        <div className="share-name-card">
          <h2>Your Name</h2>
          <p>Enter your name to leave annotations on this document.</p>
          <input
            type="text"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            placeholder="Your name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && authorName.trim()) {
                localStorage.setItem('share_author', authorName.trim())
                setShowNamePrompt(false)
              }
            }}
          />
          <button
            onClick={() => {
              if (authorName.trim()) {
                localStorage.setItem('share_author', authorName.trim())
                setShowNamePrompt(false)
              }
            }}
            disabled={!authorName.trim()}
          >
            Continue
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) return <div className="share-loading">Loading...</div>
  if (!post) return <div className="share-loading">Document not found.</div>

  const annotations = post.annotations || []

  return (
    <div className="share-view">
      {/* Minimal header */}
      <header className="share-header">
        <span className="share-logo">Content</span>
        <span className="share-meta">{post.author_name} · {new Date(post.created_at).toLocaleDateString('de-AT')}</span>
        <span style={{ flex: 1 }} />
        <span className="share-user">
          <i className="fas fa-user" style={{ marginRight: 4 }} />{authorName}
        </span>
      </header>

      {/* Document */}
      <main className="share-body">
        <article className="share-content">
          <h1 className="share-title">{post.title}</h1>
          {post.subtitle && <p className="share-subtitle">{post.subtitle}</p>}

          <div
            ref={(el) => setReadModeRoot(el)}
            className="editor-content"
            onMouseUp={() => {
              const sel = window.getSelection()?.toString()?.trim() || ''
              if (sel) {
                setLastSelection(sel)
                const srcLines = (post.content || '').split('\n')
                const selStart = sel.slice(0, 30).toLowerCase()
                const lineIdx = srcLines.findIndex(l => l.toLowerCase().includes(selStart))
                setSelectionLine(lineIdx >= 0 ? lineIdx + 1 : null)
              }
            }}
            onContextMenu={(e) => {
              const sel = window.getSelection()?.toString()?.trim() || ''
              if (sel) {
                e.preventDefault()
                setLastSelection(sel)
                setContextMenu({ x: e.clientX, y: e.clientY })
              }
            }}
            onClick={() => setContextMenu(null)}
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(post.content || '', post.title, post.annotations)
            }}
          />
          <AnnotationTooltip
            editorRoot={readModeRoot}
            annotations={(post.annotations || []) as any}
          />
        </article>

        {/* Annotations sidebar */}
        {annotations.length > 0 && (
          <aside className="share-annotations">
            <div className="sidebar-label">
              <i className="fas fa-comments" style={{ marginRight: 6 }} />
              Annotations ({annotations.length})
            </div>
            {annotations.map(a => (
              <div key={a.id} className="share-annotation">
                <div className="share-annotation-header">
                  <strong>{a.author_name}</strong>
                  <span>{new Date(a.created_at).toLocaleDateString('de-AT')}</span>
                </div>
                <div className="share-annotation-text">{a.note_text}</div>
                {a.replies?.map(r => (
                  <div key={r.id} className="share-annotation-reply">
                    <strong>{r.author_name}</strong>: {r.note_text}
                  </div>
                ))}
              </div>
            ))}
          </aside>
        )}
      </main>

      {/* Selection info + annotation form */}
      {lastSelection && (
        <div className="share-selection-bar">
          <span className="selection-text">"{lastSelection.slice(0, 60)}{lastSelection.length > 60 ? '...' : ''}"</span>
          {selectionLine && <span className="selection-line">Line {selectionLine}</span>}
          {!showAnnotationForm ? (
            <button className="share-annotate-btn" onClick={() => setShowAnnotationForm(true)}>
              <i className="fas fa-comment-dots" style={{ marginRight: 4 }} /> Add Annotation
            </button>
          ) : (
            <div className="share-annotation-form">
              <input
                type="text"
                value={annotationText}
                onChange={(e) => setAnnotationText(e.target.value)}
                placeholder="Your annotation..."
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitAnnotation() }}
              />
              <button onClick={handleSubmitAnnotation} disabled={!annotationText.trim()}>
                <i className="fas fa-paper-plane" />
              </button>
              <button onClick={() => { setShowAnnotationForm(false); setAnnotationText('') }}>
                <i className="fas fa-times" />
              </button>
            </div>
          )}
          <button className="selection-action" onClick={() => { setLastSelection(''); setSelectionLine(null); setShowAnnotationForm(false) }}>
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <div className="context-menu-overlay" onClick={() => setContextMenu(null)}>
          <div className="context-menu" style={{ left: contextMenu.x, top: contextMenu.y }} onClick={e => e.stopPropagation()}>
            <button onClick={() => { setShowAnnotationForm(true); setContextMenu(null) }}>
              <i className="fas fa-comment-dots" /> Add Annotation
            </button>
            <button onClick={() => { navigator.clipboard.writeText(lastSelection); setContextMenu(null) }}>
              <i className="fas fa-copy" /> Copy
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function renderMarkdown(md: string, title: string, annotations?: Array<{id: number; start_pos: number; end_pos: number; note_text: string; author_name: string}>): string {
  let content = md
  const firstLine = content.split('\n')[0]?.trim() || ''
  if (firstLine.startsWith('# ')) {
    const h1 = firstLine.slice(2).trim().toLowerCase()
    const t = title.trim().toLowerCase()
    if (t.startsWith(h1) || h1.startsWith(t)) {
      content = content.split('\n').slice(1).join('\n').replace(/^\n+/, '')
    }
  }
  let html = parseMarkdown(content)

  if (annotations && annotations.length > 0) {
    // Strip-MarkDown approach (same robust strategy as PostEditor:
    // matches text spans even after MarkDown→HTML transforms like
    // `**Spec...**` → `<strong>Spec...</strong>`).
    const sorted = [...annotations]
      .filter(a => a.start_pos >= 0 && a.end_pos > a.start_pos)
      .sort((a, b) => b.start_pos - a.start_pos)
    for (const ann of sorted) {
      const raw = md.slice(ann.start_pos, Math.min(ann.end_pos, ann.start_pos + 100))
      if (!raw.trim()) continue
      const cleaned = raw
        .replace(/^#{1,6}\s+/, '')
        .replace(/[*_`~]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
      if (cleaned.length < 3) continue
      const needle = cleaned.slice(0, 30)
      const idx = html.toLowerCase().indexOf(needle.toLowerCase())
      if (idx < 0) continue
      const before = html.slice(0, idx)
      const matched = html.slice(idx, idx + needle.length)
      const after = html.slice(idx + needle.length)
      html =
        before +
        `<mark class="annotation-mark" data-annotation-id="${ann.id}">` +
        matched +
        `</mark>` +
        after
    }
  }

  return html
}
